import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface FlujoItem { dia: string; proyectado: number; cobrado: number; }

export default function CashFlowChart({ data, monthLabel }: { data: FlujoItem[]; monthLabel: string }) {
  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(val);

  const totalCobrado = data.reduce((s, d) => s + (d.cobrado || 0), 0);
  const totalProyectado = data.reduce((s, d) => s + (d.proyectado || 0), 0);
  const isEmpty = totalCobrado === 0 && totalProyectado === 0;

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string; color: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-background-200 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-xs font-semibold text-foreground-700 mb-1">Día {label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} className="text-xs" style={{ color: entry.color }}>
              {entry.name === 'proyectado' ? 'Proyectado: ' : 'Cobrado: '}
              {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
            <i className="ri-line-chart-line text-primary-500 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">Flujo de Caja - {monthLabel}</h3>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-1 rounded-full bg-primary-500"></span>
            <span className="text-[10px] font-medium text-foreground-500">Proyectado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-1 rounded-full bg-emerald-400"></span>
            <span className="text-[10px] font-medium text-foreground-500">Cobrado</span>
          </div>
        </div>
      </div>
      <div className="p-5">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-line-chart-line text-foreground-400 text-xl"></i>
            </div>
            <p className="text-sm text-foreground-500 font-medium">Sin movimientos este mes</p>
            <p className="text-xs text-foreground-400 mt-1">El flujo de caja se actualizará al registrar pagos</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="colorProyectado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="oklch(0.52 0.105 255)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="oklch(0.52 0.105 255)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCobrado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.89 0.008 260)" vertical={false} />
              <XAxis dataKey="dia" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'oklch(0.58 0.006 260)' }} />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'oklch(0.58 0.006 260)' }}
                tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="proyectado"
                stroke="oklch(0.52 0.105 255)"
                strokeWidth={2}
                fill="url(#colorProyectado)"
                name="proyectado"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="cobrado"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorCobrado)"
                name="cobrado"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}